import React, { forwardRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faBullhorn,
  faCalendarCheck,
  faShoppingBasket,
  faShoppingCart,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../context/CartContext';
import './index.css';

interface NavbarProps {
  className?: string;
}

const Navbar = forwardRef<HTMLElement, NavbarProps>((props, ref) => {
  const { cart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  
  // 1. Calculate the total
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleScheduleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/schedule') {
      event.preventDefault();
      navigate('/schedule', {
        replace: true,
        state: {
          calendarRefresh: Date.now(),
        },
      });
    }
  };

  const navClassName = ['hogcloud-navbar', props.className].filter(Boolean).join(' ');

  return (
    <nav ref={ref} className={navClassName}>
      <div className="nav-top-row">
        <ul className="nav-pill nav-left">
          <li>
            <NavLink to="/" className={({ isActive }) => isActive ? 'active logo-link' : 'logo-link'}>
              <FontAwesomeIcon icon={faHome} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink to="/shop" className={({ isActive }) => isActive ? 'active' : ''}>
              <FontAwesomeIcon icon={faShoppingBasket} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink to="/about" className={({ isActive }) => isActive ? 'active' : ''}>
              <FontAwesomeIcon icon={faInfoCircle} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink to="/contact" className={({ isActive }) => isActive ? 'active' : ''} aria-label="Contact Us" title="Contact Us">
              <FontAwesomeIcon icon={faBullhorn} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/schedule"
              className={({ isActive }) => isActive ? 'active' : ''}
              aria-label="Schedule Appointment"
              title="Schedule Appointment"
              onClick={handleScheduleClick}
            >
              <FontAwesomeIcon icon={faCalendarCheck} className="hc_nav_icon" />
            </NavLink>
          </li>
        </ul>
        <ul className="nav-pill nav-right">
          <li>
            <NavLink 
              to="/cart" 
              className={({ isActive }) => {
                const baseClasses = "cart-link cart-link-expand";
                const activeClass = isActive ? "active" : "";
                const hasItemsClass = cart.length > 0 ? "cart-has-items" : "";
                
                return `${baseClasses} ${activeClass} ${hasItemsClass}`.trim();
              }}
            >
              <FontAwesomeIcon
                icon={faShoppingCart}
                className={`hc_nav_icon ${cart.length > 0 ? 'cart-glow' : ''}`}
              />
              {/* Only show the span if there is a total to display */}
              {total > 0 && (
                <span style={{ marginLeft: '6px' }}>
                  ${total.toFixed(2)}
                </span>
              )}
            </NavLink>
          </li>
        </ul>
      </div>
    </nav>
  );
});


Navbar.displayName = 'Navbar';

export default Navbar;
